// frontend/src/components/admin/AdminStats.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './AdminStats.css';

const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await api.get(
        '/admin/dashboard/stats',
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStats(response.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-stats-loading">
        <div className="spinner"></div>
        <p>Loading statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-stats-error">
        <p>❌ {error}</p>
        <button onClick={fetchStats} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="admin-stats">
      {/* Header */}
      <div className="stats-header">
        <h2>📊 Dashboard Overview</h2>
        <button onClick={fetchStats} className="refresh-btn">
          🔄 Refresh
        </button>
      </div>

      {/* Main Stats Grid */}
      <div className="stats-grid">

        {/* Users Stats */}
        <div className="stat-card users-card">
          <div className="card-header">
            <h3>👥 Users</h3>
            <span className="icon">🎮</span>
          </div>
          <div className="card-body">
            <div className="stat-row main-stat">
              <span className="label">Total Users</span>
              <span className="value">{stats?.users?.total || 0}</span>
            </div>
            <div className="stat-row">
              <span className="label">✅ Confirmed</span>
              <span className="value">{stats?.users?.confirmed || 0}</span>
            </div>
            <div className="stat-row">
              <span className="label">💰 With Wallets</span>
              <span className="value">{stats?.users?.with_wallets || 0}</span>
            </div>
            <div className="stat-row highlight">
              <span className="label">📈 New (7 days)</span>
              <span className="value">{stats?.users?.recent_7d || 0}</span>
            </div>
          </div>
        </div>

        {/* Subscribers Stats */}
        <div className="stat-card subscribers-card">
          <div className="card-header">
            <h3>📧 Subscribers</h3>
            <span className="icon">👥</span>
          </div>
          <div className="card-body">
            <div className="stat-row main-stat">
              <span className="label">Total Subscribers</span>
              <span className="value">{stats?.subscribers?.total || 0}</span>
            </div>
            <div className="stat-row">
              <span className="label">✅ Active</span>
              <span className="value">{stats?.subscribers?.active || 0}</span>
            </div>
          </div>
        </div>

        {/* Rewards Stats */}
        <div className="stat-card rewards-card">
          <div className="card-header">
            <h3>💎 Rewards</h3>
            <span className="icon">🎁</span>
          </div>
          <div className="card-body">
            <div className="stat-row main-stat">
              <span className="label">Total Distributed</span>
              <span className="value doge">
                {stats?.rewards?.total_distributed?.toFixed(2) || '0.00'} DOGE
              </span>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="stat-card status-card">
          <div className="card-header">
            <h3>⚡ System Status</h3>
            <span className="icon">✅</span>
          </div>
          <div className="card-body">
            <div className="status-item">
              <span className="status-label">API</span>
              <span className="status-badge active">Online</span>
            </div>
            <div className="status-item">
              <span className="status-label">Database</span>
              <span className="status-badge active">Connected</span>
            </div>
            <div className="status-item">
              <span className="status-label">Email Service</span>
              <span className="status-badge active">Active</span>
            </div>
          </div>
        </div>

      </div>

      {/* Activity Chart Section */}
      <div className="activity-section">
        <h3>📈 Recent Activity</h3>
        <div className="activity-grid">

          <div className="activity-card">
            <div className="activity-icon">🆕</div>
            <div className="activity-content">
              <h4>New Users</h4>
              <p className="activity-number">{stats?.users?.recent_7d || 0}</p>
              <p className="activity-label">Last 7 days</p>
            </div>
          </div>

          <div className="activity-card">
            <div className="activity-icon">✉️</div>
            <div className="activity-content">
              <h4>New Subscribers</h4>
              <p className="activity-number">{stats?.subscribers?.active || 0}</p>
              <p className="activity-label">Active now</p>
            </div>
          </div>

          <div className="activity-card">
            <div className="activity-icon">💰</div>
            <div className="activity-content">
              <h4>Wallet Users</h4>
              <p className="activity-number">{stats?.users?.with_wallets || 0}</p>
              <p className="activity-label">Connected wallets</p>
            </div>
          </div>

        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h3>⚡ Quick Actions</h3>
        <div className="actions-row">
          <button
            className="action-btn primary"
            onClick={() => window.open('https://www.focusview.ai', '_blank')}
          >
            🌐 View Website
          </button>
          <button
            className="action-btn secondary"
            onClick={fetchStats}
          >
            🔄 Refresh Stats
          </button>
          <button
            className="action-btn secondary"
            onClick={() => window.location.href = '/admin/dashboard'}
          >
            📊 Full Dashboard
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="stats-footer">
        <p>
          <span className="footer-icon">🕐</span>
          Last updated: {new Date().toLocaleTimeString()}
        </p>
        <p className="auto-refresh">Auto-refreshes every 30 seconds</p>
      </div>

    </div>
  );
};

export default AdminStats;